interface OrderAddressLine {
  street?: string | null;
  city?: string | null;
  postalCode?: string | null;
}

function cityFromLine(raw: string): string {
  const value = raw.trim();
  if (!value) {
    return "";
  }
  const parts = value
    .split(",")
    .map(part => part.trim())
    .filter(Boolean);
  if (parts.length === 1) {
    return parts[0];
  }
  if (parts.length <= 3) {
    return parts[1] || parts[0];
  }
  return parts[parts.length - 3] || parts[0];
}

/** Locality shown in Pickup and Delivery columns. */
export function displayOrderCity(
  address?: OrderAddressLine | string | null
): string {
  if (typeof address === "string") {
    return cityFromLine(address);
  }
  const raw = String(address?.city || "").trim();
  if (!raw) {
    return String(address?.street || "").trim();
  }
  return cityFromLine(raw);
}

/** Clock time in Toronto, matching the delivery window saved on a service. */
export function formatOrderWindow(
  start?: string | Date | null,
  end?: string | Date | null
): string {
  const from = formatTorontoClock(start);
  const to = formatTorontoClock(end);
  if (!from || !to) {
    return "";
  }
  return `${from} to ${to}`;
}

/** Date and clock in Toronto, so a 1:00 PM–9:00 PM window stays on one day. */
export function formatTorontoDateTime(value?: string | Date | null): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Toronto",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);
  const month = part(parts, "month");
  const day = part(parts, "day");
  const year = part(parts, "year");
  const hour = part(parts, "hour");
  const minute = part(parts, "minute");
  const dayPeriod = part(parts, "dayPeriod").replace(/\s/g, "").toUpperCase();
  if (!month || !day || !year || !hour || !minute || !dayPeriod) {
    return "";
  }
  return `${month}/${day}/${year} ${hour}:${minute}${dayPeriod}`;
}

function formatTorontoClock(value?: string | Date | null): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Toronto",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);
  const hour = part(parts, "hour");
  const minute = part(parts, "minute");
  const dayPeriod = part(parts, "dayPeriod").toUpperCase();
  if (!hour || !minute || !dayPeriod) {
    return "";
  }
  return `${hour}:${minute}${dayPeriod}`;
}

function part(
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): string {
  const found = parts.find((item) => item.type === type);
  return found ? found.value : "";
}

const PLACEHOLDER_STREETS = new Set(["delivery", "business pickup"]);

/** Full checkout address. A comma city line already contains the street. */
export function displayOrderAddress(address?: OrderAddressLine | null): string {
  const city = String(address?.city || "").trim();
  let street = String(address?.street || "").trim();
  if (PLACEHOLDER_STREETS.has(street.toLowerCase())) {
    street = "";
  }
  if (!street) {
    return city;
  }
  const cityHasStreet = city.toLowerCase().includes(street.toLowerCase());
  const streetHasCity = Boolean(city) && street.toLowerCase().includes(city.toLowerCase());
  if (cityHasStreet && city.length >= street.length) {
    return city;
  }
  if (streetHasCity && street.length >= city.length) {
    return street;
  }
  return [street, city].filter(Boolean).join(", ");
}

/** Full saved address: unit, street, city, and postal code, without repeating a part. */
export function formatSavedAddress(address?: {
  unit?: string | null;
  street?: string | null;
  city?: string | null;
  postalCode?: string | null;
} | null): string {
  const unit = String(address?.unit || "").trim();
  const street = String(address?.street || "").trim();
  const city = String(address?.city || "").trim();
  const postal = String(address?.postalCode || "").trim();
  const streetHasCity = Boolean(street && city && street.toLowerCase().includes(city.toLowerCase()));
  const cityHasStreet = Boolean(street && city && city.toLowerCase().includes(street.toLowerCase()));
  let line = cityHasStreet ? city : streetHasCity ? street : [street, city].filter(Boolean).join(", ");
  if (postal && !line.toLowerCase().includes(postal.toLowerCase())) {
    line = [line, postal].filter(Boolean).join(", ");
  }
  if (unit && !line.toLowerCase().includes(unit.toLowerCase())) {
    line = [`Unit ${unit}`, line].filter(Boolean).join(", ");
  }
  return line;
}

function squashAddressPart(value?: string | null): string {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function addressKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function addressHasPart(line: string, part: string): boolean {
  const needle = addressKey(part);
  if (!needle) {
    return false;
  }
  if (needle.length >= 3) {
    return addressKey(line).includes(needle);
  }
  const escaped = part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, "i").test(line);
}

function uniqueAddressSegments(line: string): string {
  const seen = new Set<string>();
  const kept: string[] = [];
  line.split(",").forEach(raw => {
    const part = raw.replace(/\s+/g, " ").trim();
    const key = addressKey(part);
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    kept.push(part);
  });
  return kept.join(", ");
}

const COUNTRY_LABELS: Record<string, string> = {
  ca: "Canada",
  canada: "Canada",
  us: "United States",
  usa: "United States",
  "united states": "United States",
};

function countryLabel(part: string): string {
  return COUNTRY_LABELS[part.trim().toLowerCase()] || "";
}

/**
 * One mailing address. Platform orders store the full line in `city`
 * and also keep street, unit, and postal separately, so those parts
 * are added only when the line does not already contain them.
 */
export function formatFullAddress(
  address?: {
    unit?: string | null;
    street?: string | null;
    city?: string | null;
    postalCode?: string | null;
  } | null,
  fallback?: { city?: string | null; postalCode?: string | null } | null
): string {
  const unit = squashAddressPart(address?.unit);
  const street = squashAddressPart(address?.street);
  const city = squashAddressPart(address?.city) || squashAddressPart(fallback?.city);
  const postalRaw = squashAddressPart(address?.postalCode) || squashAddressPart(fallback?.postalCode);
  const postalCompact = postalRaw.replace(/\s+/g, "").toUpperCase();
  const postalLabel =
    postalCompact.length === 6 ? `${postalCompact.slice(0, 3)} ${postalCompact.slice(3)}` : postalRaw.toUpperCase();

  const cityAlreadyHasStreet = Boolean(street) && addressHasPart(city, street);
  const base = cityAlreadyHasStreet || !street ? city : [street, city].filter(Boolean).join(", ");
  let country = "";
  const segments = base
    .split(",")
    .map(part => part.replace(/\s+/g, " ").trim())
    .filter(part => {
      const named = countryLabel(part);
      if (!named) {
        return Boolean(part);
      }
      country = named;
      return false;
    });
  if (postalLabel && !segments.some(part => addressKey(part).includes(addressKey(postalLabel)))) {
    segments.push(postalLabel);
  }
  if (unit && !addressHasPart(segments.join(", "), unit)) {
    const unitLabel = /^(unit|apt|apartment|suite|ste|#)\b/i.test(unit) ? unit : `Unit ${unit}`;
    segments.unshift(unitLabel);
  }
  if (!country && segments.length) {
    country = "Canada";
  }
  if (country) {
    segments.push(country);
  }
  return uniqueAddressSegments(segments.join(", "));
}
